// =====================================================
// Amplio - Login Form Component
// Secure login with Swiss styling and validation
// =====================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SwissLogo } from '@/components/ui/swiss-logo';

export function LoginForm() {
  // Pre-fill email if coming from signup
  const location = useLocation();
  const locationState = location.state as { email?: string; message?: string } | null;
  
  const [email, setEmail] = useState(locationState?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(locationState?.message || null);

  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auto-redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const resolveRedirectPath = (state: unknown): string => {
    if (state && typeof state === 'object') {
      const record = state as Record<string, unknown>;
      const value = record.from;
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }
    return '/dashboard';
  };

  const from = resolveRedirectPath(location.state);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await signIn(email, password);

      if (result.error) {
        setError(getErrorMessage(result.error.message));
        setLoading(false);
      } else {
        toast({
          title: "Erfolgreich angemeldet",
          description: "Willkommen bei Amplio",
        });
        
        // Navigate after successful login
        // Note: Don't set loading to false - let the page transition handle it
        // This prevents the button from flickering before navigation
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);
      }
    } catch (error) {
      setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  const getErrorMessage = (error: string): string => {
    if (error.includes('Invalid login credentials')) {
      return 'Ungültige E-Mail-Adresse oder Passwort.';
    }
    if (error.includes('Email not confirmed')) {
      return 'Bitte bestätigen Sie Ihre E-Mail-Adresse.';
    }
    if (error.includes('Too many requests')) {
      return 'Zu viele Anmeldeversuche. Bitte warten Sie einen Moment.';
    }
    return 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Daten.';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-elegant">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <SwissLogo size="lg" />
          </div>
          <CardTitle className="text-2xl font-space-grotesk font-bold text-brand-primary">
            Amplio
          </CardTitle>
          <CardDescription className="text-muted-foreground font-inter">
            Melden Sie sich in Ihrem Konto an
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {successMessage && (
              <Alert className="border-brand-accent bg-brand-accent/10">
                <AlertDescription className="text-brand-primary font-inter">
                  {successMessage}
                </AlertDescription>
              </Alert>
            )}
            
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="font-inter">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail-Adresse</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre.email@firma.ch"
                  className="pl-10"
                  required
                  autoComplete="email"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ihr Passwort"
                  className="pl-10 pr-10"
                  required
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-brand-primary transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-brand-primary hover:bg-brand-primary/90 text-brand-primary-foreground font-inter font-medium shadow-elegant"
              disabled={loading || !email || !password}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </Button>

            <div className="text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-sm text-brand-primary hover:text-brand-primary/80 font-inter transition-colors"
              >
                Passwort vergessen?
              </Link>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-center text-sm text-muted-foreground font-inter">
              Noch kein Konto?{' '}
              <Link
                to="/signup"
                className="text-brand-primary hover:text-brand-primary/80 font-medium transition-colors"
              >
                Hier registrieren
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}